/**
 * Native share helper for Eili.
 *
 *   1. Try navigator.share({files}) — works on iOS + Chrome.
 *
 *   2. If that throws AND we're in Despia → shareapp:// scheme.
 *      navigator.share doesn't work in Android Despia WebView at all
 *      (not for files, not for links). shareapp:// is the only path
 *      that opens the native share sheet on Android Despia.
 *
 *   3. Non-Despia link-only fallback.
 */

import despia from 'despia-native';

/**
 * @param {{title?: string, text?: string, url?: string, file?: File, files?: File[]}} payload
 */
export async function shareViaNative({ title, text, url, file, files }) {

  // ── Try Web Share with files (iOS, Chrome, desktop) ──────────────
  // Multiple files are shared as one set when the device supports it.
  const list = files?.length > 1 && navigator.canShare?.({ files }) ? files : (file ? [file] : null);
  if (typeof navigator.share === 'function' && list) {
    try {
      await navigator.share({ title, text, url, files: list });
      return { shared: true, channel: 'webshare-files' };
    } catch (err) {
      if (err?.name === 'AbortError') {
        return { shared: false, aborted: true, channel: 'webshare-files' };
      }
      // navigator.share failed — fall through to Despia or link-only.
    }
  }

  // ── Despia fallback — shareapp:// ────────────────────────────────
  // navigator.share throws on Android Despia WebView (both file and
  // link-only calls). shareapp:// is the only way to open the native
  // share sheet on this platform.
  const isDespia = typeof navigator !== 'undefined' &&
    navigator.userAgent.toLowerCase().includes('despia');

  if (isDespia && url) {
    const caption = [title, text].filter(Boolean).join(' — ');
    despia(
      `shareapp://message?=${encodeURIComponent(caption)}&url=${encodeURIComponent(url)}`
    );
    return { shared: true, channel: 'despia-shareapp' };
  }

  // ── Non-Despia link-only fallback ────────────────────────────────
  if (typeof navigator.share === 'function' && url) {
    try {
      await navigator.share({ title, text, url });
      return { shared: true, channel: 'webshare-link' };
    } catch (err) {
      if (err?.name === 'AbortError') {
        return { shared: false, aborted: true, channel: 'webshare-link' };
      }
    }
  }

  return { shared: false, channel: 'none', reason: 'no-share-mechanism' };
}
